import { useMemo, useState } from 'react';
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isToday,
  startOfWeek,
} from 'date-fns';
import { CalendarDays, CalendarPlus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge, Button, Card, CardBody, EmptyState, LoadingState, PageHeader, cn } from '@tutor/ui';
import { useLessons } from '../../hooks/queries';
import { LessonCard } from '../../components/LessonCard';
import { ScheduleLessonDialog } from '../../components/dialogs/ScheduleLessonDialog';
import { toDate } from '../../lib/format';

export function CalendarPage({ perspective }: { perspective: 'tutor' | 'student' }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const { weekStart, weekEnd, days } = useMemo(() => {
    const base = addWeeks(new Date(), weekOffset);
    const start = startOfWeek(base, { weekStartsOn: 1 });
    const end = endOfWeek(base, { weekStartsOn: 1 });
    return { weekStart: start, weekEnd: end, days: eachDayOfInterval({ start, end }) };
  }, [weekOffset]);

  const { data, isLoading } = useLessons({
    scope: 'all',
    from: weekStart.toISOString(),
    to: weekEnd.toISOString(),
    pageSize: 100,
  });

  const lessons = data?.items ?? [];

  const lessonsByDay = useMemo(() => {
    const map = new Map<string, typeof lessons>();
    for (const lesson of lessons) {
      const key = format(toDate(lesson.scheduledStart), 'yyyy-MM-dd');
      map.set(key, [...(map.get(key) ?? []), lesson]);
    }
    return map;
  }, [lessons]);

  const selectedLessons = lessonsByDay.get(format(selectedDay, 'yyyy-MM-dd')) ?? [];

  return (
    <>
      <PageHeader
        title="Calendar"
        description={`${format(weekStart, 'd MMM')} – ${format(weekEnd, 'd MMM yyyy')}`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setWeekOffset((v) => v - 1)} aria-label="Previous week">
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setWeekOffset(0);
                setSelectedDay(new Date());
              }}
            >
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekOffset((v) => v + 1)} aria-label="Next week">
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
            {perspective === 'tutor' && (
              <Button
                size="sm"
                onClick={() => setScheduleOpen(true)}
                leftIcon={<CalendarPlus className="h-4 w-4" aria-hidden />}
              >
                Schedule
              </Button>
            )}
          </div>
        }
      />

      {perspective === 'tutor' && (
        <ScheduleLessonDialog
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          defaultDate={selectedDay}
        />
      )}

      <div className="mb-6 grid grid-cols-7 gap-2">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const count = lessonsByDay.get(key)?.length ?? 0;
          const isSelected = isSameDay(day, selectedDay);

          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={cn(
                'flex flex-col items-center gap-1 rounded-xl border p-3 transition-colors',
                isSelected
                  ? 'border-brand-300 bg-brand-50'
                  : 'border-slate-200 bg-white hover:border-slate-300',
              )}
            >
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {format(day, 'EEE')}
              </span>
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold tabular-nums',
                  isToday(day) ? 'bg-brand-600 text-white' : 'text-slate-900',
                )}
              >
                {format(day, 'd')}
              </span>
              {count > 0 ? (
                <Badge tone={isSelected ? 'brand' : 'neutral'}>{count}</Badge>
              ) : (
                <span className="h-5" aria-hidden />
              )}
            </button>
          );
        })}
      </div>

      <Card>
        <CardBody className="space-y-3">
          {isLoading ? (
            <LoadingState />
          ) : selectedLessons.length === 0 ? (
            <EmptyState
              title={`Nothing on ${format(selectedDay, 'EEEE d MMMM')}`}
              description="Pick another day to see what is scheduled."
              icon={<CalendarDays className="h-5 w-5" aria-hidden />}
            />
          ) : (
            selectedLessons
              .slice()
              .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart))
              .map((lesson) => <LessonCard key={lesson.id} lesson={lesson} perspective={perspective} />)
          )}
        </CardBody>
      </Card>
    </>
  );
}
